import React, { useCallback, useEffect, useRef } from 'react';
import { Article } from '@/types';
import { getMediaUrl } from '@/services/api';

interface ArticleModalProps {
  article: Article | null;
  onClose: () => void;
  className?: string;
}

const ArticleModal: React.FC<ArticleModalProps> = ({
  article,
  onClose,
  className = ''
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Add/remove event listeners
  useEffect(() => {
    if (article) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [article, handleKeyDown]);

  if (!article) return null;

  return (
    <div 
      className={`article-modal-overlay ${className}`}
      onClick={handleOverlayClick}
      ref={modalRef}
    >
      <div className="article-modal-content" onClick={e => e.stopPropagation()}>
        <div className="article-content">
          <div className="article-header">
            <button 
              className="close-btn article-close" 
              onClick={onClose}
              aria-label="Close article"
            >
              <i className="fas fa-arrow-left"></i>
            </button>
            <div className="article-title-container">
              <span className="article-date">{article.date}</span>
              <h2>{article.title}</h2>
              <p className="subtitle">{article.subtitle}</p>
            </div>
          </div>
          
          <div className="article-body">
            {(article.image1 || article.image2) && (
              <div className="article-images">
                {article.image1 && (
                  <img 
                    src={getMediaUrl(article.image1)} 
                    alt={article.title}
                    className="article-image"
                    loading="lazy"
                  />
                )}
                {article.image2 && (
                  <img 
                    src={getMediaUrl(article.image2)}
                    alt={article.title}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                    loading="lazy"
                  />
                )}
              </div>
            )}
            
            <div className="article-text">
              {article.content.split('\n').map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
        
        <div className="modal-controls">
          <button 
            className="close-btn" 
            onClick={onClose}
            aria-label="Close article"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArticleModal;
